import React, { useRef, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { contentTypeConfiguration } from '../model/ContentTypes';
import { chooseObjectExample, type ObjectExamplesConfig } from '../model/ObjectExamples';

const styles = cssMap({
	container: {
		width: '100%',
		maxWidth: '640px',
	},
	cardGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, 1fr)',
		gap: token('space.200'),
	},
	card: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
		height: '72px',
		borderRadius: token('border.radius.200'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		transition: 'all 0.2s ease-in-out',
		'&:hover': {
			transform: 'translateY(-1px)',
		},
	},
	cardIcon: {
		marginBottom: token('space.150'),
		display: 'flex',
		justifyContent: 'center',
	},
});

type ObjectAwareSectionProps = {
	examples: ObjectExamplesConfig;
	onHover?: () => void;
	onSelect?: () => void;
};

export const ObjectAwareSection = ({ examples, onHover, onSelect }: ObjectAwareSectionProps) => {
	const [hoveredCard, setHoveredCard] = useState<string | null>(null);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const handleCardHover = (example: string | null) => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
		}

		setHoveredCard(example);

		onHover?.();
	};

	const handleCardClick = (example: string) => {
		if (!examples) {
			return;
		}

		const exampleData = examples[example];
		if (exampleData) {
			chooseObjectExample(example, examples);
			onSelect?.();
		}
	};

	return (
		<Box xcss={styles.container}>
			<Box xcss={styles.cardGrid}>
				{Object.entries(examples).map(([key, example]) => {
					const IconComponent = contentTypeConfiguration[example.contentType].icon;
					const isHovered = hoveredCard === key;
					return (
						<Pressable
							key={example.title}
							onMouseEnter={() => handleCardHover(key)}
							onMouseLeave={() => handleCardHover(null)}
							onClick={() => handleCardClick(key)}
							xcss={styles.card}
							style={{
								backgroundColor: isHovered ? example.hoverColor : undefined,
							}}
						>
							<Stack space="space.050">
								<Text align="start" weight="bold" color="color.text">
									{example.title}
								</Text>
								<Inline alignBlock="center" space="space.050">
									<IconComponent
										label={contentTypeConfiguration[example.contentType].tooltip}
										size="small"
										color={token('color.icon.subtle')}
									/>
									<Text color="color.text.subtlest" size="small">
										{contentTypeConfiguration[example.contentType].tooltip}
									</Text>
								</Inline>
							</Stack>
						</Pressable>
					);
				})}
			</Box>
		</Box>
	);
};
